import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import WriteButton from "@/components/WriteButton";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Search, UserPlus, User, X } from "lucide-react";
import { useListNav } from "@/lib/useListNav";

export default function CustomerDialog({ open, onClose, onSelect, selected }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [creating, setCreating] = useState(false);
  const searchRef = useRef(null);
  const nav = useListNav(list, (c) => { onSelect(c); onClose(); });

  const load = async (term) => {
    try {
      const { data } = await api.get("/customers", { params: term ? { q: term } : {} });
      setList(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    if (open) {
      setQ("");
      setName("");
      setPhone("");
      setCreating(false);
      load("");
      setTimeout(() => searchRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const create = async () => {
    if (!name.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/customers", { name: name.trim(), phone: phone.trim() || null });
      toast.success("Pelanggan ditambahkan");
      onSelect(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="customer-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Pelanggan (F1)</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Cari pelanggan, atau tambah baru untuk piutang.</DialogDescription>
        </DialogHeader>

        {selected && (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm">
            <User className="h-4 w-4 text-emerald-600" />
            <span className="flex-1 truncate">Terpilih: <b>{selected.name}</b></span>
            <button data-testid="customer-clear-btn" onClick={() => { onSelect(null); }} className="text-slate-500 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            ref={searchRef}
            data-testid="customer-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={nav.onInputKeyDown}
            placeholder="Cari nama / no. HP…"
            className="h-11 pl-10 bg-[#F7F9FB] border-slate-300"
          />
        </div>

        <div ref={nav.listRef} data-testid="customer-list" className="max-h-52 overflow-y-auto space-y-1">
          {list.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-6">Tidak ada pelanggan</div>
          ) : (
            list.map((c, idx) => (
              <button
                key={c.id}
                data-testid={`customer-item-${c.id}`}
                data-nav-index={idx}
                onMouseEnter={() => nav.setActive(idx)}
                onClick={() => { onSelect(c); onClose(); }}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border bg-slate-50 px-3 py-2 text-left no-select ${idx === nav.active ? "border-emerald-500 ring-1 ring-emerald-500/60" : "border-slate-300 hover:border-emerald-500/60"}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  {c.phone ? <div className="text-[11px] text-slate-500">{c.phone}</div> : null}
                </div>
                {c.debt > 0 ? <span className="text-[11px] font-mono text-red-600 shrink-0">Hutang {rupiah(c.debt)}</span> : null}
              </button>
            ))
          )}
        </div>

        <div className="border-t border-slate-200 pt-3 space-y-2">
          <div className="text-xs text-slate-500 flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Tambah pelanggan baru</div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="new-customer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama" className="h-10 bg-[#F7F9FB] border-slate-300" />
            <Input data-testid="new-customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="No. HP (opsional)" className="h-10 bg-[#F7F9FB] border-slate-300" />
          </div>
          <WriteButton data-testid="create-customer-btn" onClick={create} disabled={creating} className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
            <UserPlus className="h-4 w-4" /> Tambah & Pilih
          </WriteButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
